<script>
  import { getContext, onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { ROUTES } from "@utils/routes.js";

  import WebsiteRules from "./UserInputModules/WebsiteRules.svelte";
  import ApplicationRules from "./UserInputModules/ApplicationRules.svelte";
  // Hidden for now, see the TODO at stepOptions.
  // import AnonymizationRules from "./UserInputModules/AnonymizationRules.svelte";
  import ExceptionRules from "./UserInputModules/ExceptionRules.svelte";
  import SimpleBlacklist from "./UserInputModules/SimpleBlacklist.svelte";
  import SimpleAnonRules from "./UserInputModules/SimpleAnonRules.svelte";
  import Header from "../lib/components/Header.svelte";
  import ContentSwitcher from "../lib/components/ContentSwitcher.svelte";
  import { InfoBlock, SectionWrapper, Button } from "paxray-components-svelte";

  import Modal from "@components/Modal.svelte";
  import Checkmark16 from "carbon-icons-svelte/lib/Checkmark16";

  const urlParams = useUrlParams();
  const session = getContext("session");

  let idproject;
  let currentStep;
  // Reported by the apps and websites tabs, shown like a completed step in the header.
  let exclusionsClosed = false;
  let lastStoreStep;

  // Only sync the step while on this route, leaving it must not rewrite the next URL.
  $: isUserInputRoute = $urlParams?.pathname === ROUTES.userInput;

  $: if ($urlParams && isUserInputRoute) {
    idproject = $urlParams.idproject || $urlParams.project;

    const query = new URLSearchParams($urlParams.search);
    if (!query.has("currentStep")) {
      urlParams.updateParam(
        "currentStep",
        currentStep !== undefined ? currentStep : 0,
        { replace: true },
      );
    } else if ($urlParams.currentStep !== lastStoreStep) {
      lastStoreStep = $urlParams.currentStep;
      currentStep = $urlParams.currentStep;
    }
  }

  // Apps and websites are submitted one by one. Anonymization Rules (index 2) and
  // Local-Opening Rules (index 3) are hidden for now and count as done.
  let stepsCompleted = [false, false, true, true];
  let submittedCount = stepsCompleted.filter(Boolean).length;

  // After each submit go to the next tab that is still open. "All done" only
  // once every visible tab has been submitted.
  $: {
    const done = stepsCompleted.filter(Boolean).length;
    if (done > submittedCount) {
      submittedCount = done;
      const nextOpen = stepsCompleted.findIndex((completed) => !completed);
      currentStep = nextOpen === -1 ? 4 : nextOpen;
    }
  }
  let showRestrictionsGuide = false;
  let restrictionsGuideStep = 0;
  let restrictionsGuideSteps = [];

  function startRestrictionsTour() {
    restrictionsGuideSteps = [
      {
        target: '[data-guide="restrictions-switcher"]',
        title: "Choose what to set up",
        text: "Switch between excluded apps and excluded websites.",
      },
      {
        target: '[data-guide="restrictions-hint"]',
        title: "What this step does",
        text: "Read what the current step does before you add rules.",
      },
      {
        target: '[data-guide="restrictions-content"] > .step_panel:not([style*="none"]) .custom-table',
        title: "Your rules",
        text: "Add a rule with the button on the right. Your own rules appear when you expand the row.",
      },
      {
        target: '[data-guide="restrictions-content"] .step_panel:not([style*="none"]) [data-guide="restrictions-submit"], [data-guide="restrictions-content"] > [data-guide="restrictions-submit"]',
        title: "Submit your rules",
        text: "Submit when you are done to move on to the next step.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    restrictionsGuideStep = 0;
    showRestrictionsGuide = restrictionsGuideSteps.length > 0;
  }

  onMount(() => registerViewTour(startRestrictionsTour));
  const stepOptions = [
    "Excluded Apps",
    "Excluded Websites",
    // TODO @Jonas @Adrian @Alex: Participant anonymization rules are hidden for now.
    // They are saved but not applied (E2E run 13, finding K-D). Still to do:
    // - Show participant app and website exclusions as their own row in Blacklisting,
    //   preselected and removable (needs a project flag, filter in logs.py and endpoints).
    // - Reject new participant entries once "Complete & Lock Exclusions" is done, in the
    //   backend, and show a closed state on this page.
    // - Decide whether participant anonymization rules come back once they are applied.
    // "Anonymization Rules",
    // Local-Opening Rules are hidden for now, participant entries are not used yet.
    // "Local-Opening Rules",
  ];
  const helpSteps = [
    {
      title: "Excluded Apps",
      description:
        "Limit the initial scope of the paxray sensor by defining exclusions for desktop apps.",
    },
    {
      title: "Excluded Websites",
      description:
        "Limit the initial scope of the paxray sensor by defining exclusions for websites and web apps.",
    },
    {
      title: "Anonymization Rules",
      description:
        "Ensure the privacy of potentially sensitive information within apps or websites.",
    },
    {
      title: "Local-Opening Rules",
      description:
        "Ensure capturing of relevant applications by defining specific whitelist rules.",
    },
  ];

  // Old links may still point to the hidden anonymization rules tab.
  $: if (Number(currentStep) === 2) currentStep = 0;
  $: currentHelp = helpSteps[Number(currentStep)] || null;
  $: breadcrumbsClickable = session?.isValid === true;

  $: if (
    isUserInputRoute &&
    currentStep !== undefined &&
    currentStep !== lastStoreStep
  ) {
    lastStoreStep = currentStep;
    urlParams.updateParam("currentStep", currentStep);
  }

  $$restProps;
</script>

<!-- Participants open this page without signing in; the project progress is not theirs to see. -->
<Header
  section="User Input"
  area={breadcrumbsClickable ? "Setup" : ""}
  breadcrumb="Participant Restrictions"
  tooltipText="Review and adjust app and website exclusions for the paxray sensor."
  {breadcrumbsClickable}
>
  <Button
    kind="secondary"
    size="field"
    text="Help"
    on:click={startRestrictionsTour}
  />
  {#if exclusionsClosed}
    <div class="completed_badge">
      <Checkmark16 />
      <span>Completed</span>
    </div>
  {/if}
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    {#if currentStep < 4}
      <div class="phase_switcher" data-guide="restrictions-switcher">
        <ContentSwitcher
          options={stepOptions}
          bind:selected={currentStep}
          fitContent
        />
      </div>
    {/if}
    {#if currentHelp}
      <div data-guide="restrictions-hint">
        <InfoBlock
          type="info"
          heading={currentHelp.title}
          message={currentHelp.description}
          showClose={false}
        />
      </div>
    {/if}
    <div class="wrapper" data-guide="restrictions-content">
      <!-- Apps and websites stay mounted while switching tabs, otherwise entries that are
           not submitted yet were lost when the other tab was opened or submitted. -->
      {#if currentStep == 0 || currentStep == 1}
        <div class="step_panel" style:display={currentStep == 0 ? null : "none"}>
          <ApplicationRules
            {idproject}
            bind:stepsCompleted={stepsCompleted[0]}
            bind:currentStep
            bind:closed={exclusionsClosed}
          ></ApplicationRules>
        </div>
        <div class="step_panel" style:display={currentStep == 1 ? null : "none"}>
          <WebsiteRules
            {idproject}
            bind:stepsCompleted={stepsCompleted[1]}
            bind:currentStep
            bind:closed={exclusionsClosed}
          ></WebsiteRules>
        </div>
      <!-- Participant anonymization rules are hidden for now, see the TODO at stepOptions.
      {:else if currentStep == 2}
        <AnonymizationRules
          {idproject}
          bind:stepsCompleted={stepsCompleted[2]}
          bind:currentStep
        ></AnonymizationRules>
      -->
      {:else if currentStep == 3}
        <ExceptionRules
          {idproject}
          bind:stepsCompleted={stepsCompleted[3]}
          bind:currentStep
        ></ExceptionRules>
      {:else if currentStep == 4}
        <div class="done_wrapper">
          <div class="done_icon" aria-hidden="true">
            <img src="/img/assistant/icons/check.svg" alt="" />
          </div>
          <div class="done_text">
            <div class="done_title">All done!</div>
            <div class="done_hint">Thank you for your participation.</div>
          </div>
        </div>
      {:else if currentStep == 5}
        <SimpleBlacklist {idproject} bind:currentStep></SimpleBlacklist>
      {:else if currentStep == 6}
        <SimpleAnonRules {idproject} bind:currentStep></SimpleAnonRules>
      {/if}
    </div>
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showRestrictionsGuide}
  bind:currentStep={restrictionsGuideStep}
  steps={restrictionsGuideSteps}
  allowInteraction={false}
/>

<style>
  .completed_badge {
    border-radius: 0.5rem;
    font-size: 14px;
    white-space: nowrap;
    padding: 0 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    box-sizing: border-box;
    /* Same height as the field-size Help button, like completed-badge-sm in ProjectStepActions. */
    height: 2.5rem;
    background: var(--pax-green);
    color: #1c1c20;
  }

  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .step_panel {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .phase_switcher {
    display: flex;
    justify-content: flex-start;
  }

  .done_wrapper {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: center;
    min-height: calc(100vh - 4rem);
    width: 100%;
  }

  .done_icon {
    align-items: center;
    background: var(--pax-green-new);
    border-radius: 50%;
    display: flex;
    flex: 0 0 3rem;
    height: 3rem;
    justify-content: center;
    width: 3rem;
  }

  .done_icon img {
    display: block;
    filter: brightness(0);
    height: 1.5rem;
    width: 1.5rem;
  }

  .done_title,
  .done_hint {
    font-size: 1rem;
  }

  .done_title {
    color: var(--pax-black);
  }

  .done_hint {
    color: var(--pax-grey);
    margin-top: 0.5rem;
  }

  .wrapper > :global(.information_fields) {
    display: none;
  }
</style>
